import React, { useState, useRef, useCallback, useEffect } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider } from "react-dnd";
import { useDrag, useDrop } from "react-dnd";
import update from "immutability-helper";
import Scrollable from "../../Shared/Edit/Scrollable";
import { filterBy } from "@progress/kendo-data-query";
import BulkPicklistStringDialog from "./BulkPicklistStringDialog";

export const PicklistDefaultValueField = ({ value, picklist, disabled, onChange }) => {

	const [data, setData] = useState(getOptions || []);	

	useEffect(() => {
		setData(getOptions());
	}, [picklist]);

	useEffect(() => {
		setData(getOptions());
	}, [picklist]);

	function getOptions() {
		if (!picklist) return [];
		const options = picklist.split("ɖ");
		return options;
	}

	const filterData = (filter) => {
		const dataToFilter = getOptions();
		if (!filter || filter === "") return dataToFilter;
		if (dataToFilter.length > 0) {
			return filterBy(dataToFilter, filter);
		}
		return [];
	};

	const filterChange = (event) => {
		const filteredData = filterData(event.filter);
		setData(filteredData);
	};

	return (
		<ComboBox
			className="form-control-custom"
			name="defaultValue"
			data={data}
			value={value}
			allowCustom={false}
			filterable={true}
			onFilterChange={filterChange}
			style={{ width: "100%" }}
			disabled={disabled}
			onChange={onChange} />
	);
};

export const DraggableCard = ({ id, text, index, onItemDragged, onItemEdited, onItemRemoved, onEnterKeyIsPressed, isFocused, duplicate }) => {
	const ref = useRef(null);

	const [{ handlerId }, drop] = useDrop({
		accept: "DraggableCard",
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},

		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) { return; }
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) { return; }
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) { return; }
			// Time to actually perform the action
			onItemDragged(dragIndex, hoverIndex);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag] = useDrag({
		type: "DraggableCard",
		item: () => { return { id, index }; },
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	});

	drag(drop(ref));

	const checkEnterKeyPressed = (e) => {
		if (e.key === 'Enter')
			onEnterKeyIsPressed();
	}
	//

	return (
		<div ref={ref} data-handler-id={handlerId} className="my-1 rounded draggable-row d-flex align-items-center"  >
			<i className="me-3 fas fa-grip-vertical grabbable"></i>
			<input type="text" className={"form-control " + (duplicate ? 'border-danger is-invalid' : '')} value={text} autoFocus={isFocused}
				onKeyDown={(e) => checkEnterKeyPressed(e)}
				onChange={(e) => onItemEdited(e.target.value, index)} />
			<i className={`fa fa-times mx-3 pointer ${index === 0 && 'invisible'}`} onClick={() => onItemRemoved(index)}></i>
		</div>
	);
};

export const PicklistOptionsField = (props) => {
	const splitChar = "ɖ";
	const { disabled, autoFocus, value, addDropdownButtonText = "Dropdown Values", onBlur, onChange, onErr } = props;
	const [collection, setCollection] = useState([]);
	const [focus, setFocus] = useState(autoFocus);
	const [showBulkPiclistStringDialog, setShowBulkPiclistStringDialog] = useState(false);

	useEffect(() => {
		if (collection.length === 0)
			if (value === "")
				addToCollection()

		setFocus(autoFocus);
		setCollection(value.split(splitChar));
	}, [props]);

	const [val, setVal] = useState("");
	const ref = useRef(null);

	function handleChange(value) {
		onChange({
			target: {
				name: "picklist",
				value: value.join(splitChar),
			},
		});
	}

	function addToCollection() {
		onErr("");
		setCollection([...collection, ""]);
		setVal("");
		handleChange([...collection, ""]);
		setFocus(true);
	}

	function handleEnterKey(e) {
		if (e.keyCode === 13) {
			e.preventDefault();
			addToCollection();
		}
	}

	function itemDeleted(index) {
		onErr("");
		setCollection(collection.filter((o, ix) => index !== ix));
		handleChange(collection.filter((o, ix) => index !== ix));
	}

	function itemValueChanged(val, index) {
		onErr("");
		let newValue = [...collection.slice(0, index), val, ...collection.slice(index + 1)];
		setCollection(newValue);
		handleChange(newValue);
	}

	const cardDragged = useCallback(
		(dragIndex, hoverIndex) => {
			const dragCard = collection[dragIndex];
			const v = update(collection, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, dragCard],
				],
			});
			setCollection(v);
			handleChange(v);
		},
		[collection]
	);

	function hasDuplicate(text, textIndex) {
		return collection.some((item, itemIndex) => (
			item.trim().toLowerCase() === text.trim().toLowerCase() &&
			itemIndex !== textIndex
		))
	}

	function addBulkPicklistValues(values) {
		const filteredCollection = collection.filter((o) => o.trim() !== "");
		const filteredValues = values.filter((o) => o.trim() !== "");
		const newCollection = [...filteredCollection, ...filteredValues];
		setCollection(newCollection);
		handleChange(newCollection);
	}


	if (props.showScrollable) {
		return (
			<>
				<div className="inner-drop-container" style={{ height: "84%" }}>
					<Scrollable>
						<DndProvider backend={HTML5Backend}>
							{collection && collection.map((o, i) => (
								<>
									<DraggableCard
										key={i}
										index={i}
										id={i}
										text={o}
										onItemDragged={cardDragged}
										onItemEdited={itemValueChanged}
										onItemRemoved={itemDeleted}
										onEnterKeyIsPressed={addToCollection}
										duplicate={hasDuplicate(o, i)}
										isFocused={i === collection.length - 1 && focus}
									/>
								</>
							))}
						</DndProvider>
					</Scrollable>
				</div>
				<button type="button" className="btn p-2 hover-bg-7 hover-border-0 form-label d-inline-block t-primary pointer" onClick={() => addToCollection()}>
					<i className="me-1 fa fa-plus"></i>{addDropdownButtonText}
				</button>
			</>
		)
	}

	return (
		<div className="inner-drop-container">
			<DndProvider backend={HTML5Backend}>
				<div className="drop-container-picklist">
					{collection && collection.map((o, i) => (
						<DraggableCard
							key={i}
							index={i}
							id={i}
							text={o}
							onItemDragged={cardDragged}
							onItemEdited={itemValueChanged}
							onItemRemoved={itemDeleted}
							onEnterKeyIsPressed={addToCollection}
							duplicate={hasDuplicate(o, i)}
							isFocused={i === collection.length - 1 && focus}
						/>
					))}
				</div>
			</DndProvider>
			<button type="button" className="btn p-2 hover-bg-7 hover-border-0 form-label d-inline-block t-primary pointer" onClick={() => addToCollection()}>
				<i className="me-1 fa fa-plus"></i>{addDropdownButtonText}
			</button>
			<button
				title="Bulk Dropdown Values"
				type="button"
				onClick={() => setShowBulkPiclistStringDialog(true)}
				className="btn hover-bg-7 hover-border-0 float-end form-label d-inline-block t-primary pointer font-size-2">
				<i class="far fa-folder-upload"></i>
			</button>
			{showBulkPiclistStringDialog &&
				<BulkPicklistStringDialog
					onClose={() => setShowBulkPiclistStringDialog(false)}
					onSave={addBulkPicklistValues}
				/>
			}
		</div>
	);
};
